<template>
  <div class="organization-autocomplete">
    <q-select
      :model-value="modelValue"
      :options="filteredOptions"
      :label="label"
      :loading="loading"
      :rules="computedRules"
      outlined
      use-input
      emit-value
      map-options
      option-value="id"
      option-label="name"
      input-debounce="300"
      clearable
      @filter="onFilter"
      @update:model-value="onSelect"
      @input-value="onInputValue"
    >
      <template #prepend>
        <q-icon :name="typeIcon" />
      </template>

      <template #option="scope">
        <q-item v-bind="scope.itemProps">
          <q-item-section avatar>
            <q-icon :name="scope.opt.isCreateNew ? 'add_circle' : typeIcon" :color="scope.opt.isCreateNew ? 'primary' : 'grey-7'" />
          </q-item-section>
          <q-item-section>
            <q-item-label :class="{ 'text-primary text-weight-bold': scope.opt.isCreateNew }">
              {{ scope.opt.name }}
            </q-item-label>
            <q-item-label v-if="scope.opt.primary_contact_name && !scope.opt.isCreateNew" caption>
              Contact: {{ scope.opt.primary_contact_name }}
            </q-item-label>
            <q-item-label v-if="scope.opt.isCreateNew" caption class="text-primary">
              Click to create new organization
            </q-item-label>
          </q-item-section>
          <q-item-section v-if="!scope.opt.isCreateNew && scope.opt.types && scope.opt.types.length > 0" side>
            <div class="row q-gutter-xs">
              <q-badge
                v-for="orgType in scope.opt.types"
                :key="orgType"
                :color="getTypeColor(orgType)"
                :label="formatType(orgType)"
                outline
              />
            </div>
          </q-item-section>
        </q-item>
      </template>

      <template #selected-item="scope">
        <div class="row items-center q-gutter-sm">
          <span>{{ scope.opt?.name || '' }}</span>
          <q-badge
            v-if="scope.opt?.primary_contact_name"
            color="grey-5"
            :label="`Contact: ${scope.opt.primary_contact_name}`"
          />
        </div>
      </template>

      <template #no-option>
        <q-item>
          <q-item-section class="text-grey">
            {{ searchTerm ? 'No organizations found' : 'Start typing to search...' }}
          </q-item-section>
        </q-item>
      </template>

      <template #after>
        <q-btn
          v-if="!modelValue"
          round
          dense
          flat
          icon="add"
          color="primary"
          @click="showQuickCreate = true"
        >
          <q-tooltip>Create New Organization</q-tooltip>
        </q-btn>
      </template>
    </q-select>

    <!-- Quick Create Dialog -->
    <q-dialog v-model="showQuickCreate" persistent>
      <q-card style="min-width: 400px">
        <q-card-section class="row items-center">
          <q-avatar color="primary" text-color="white" icon="add_business" />
          <span class="q-ml-sm text-h6">Quick Create {{ formatType(type) }}</span>
        </q-card-section>

        <q-card-section>
          <q-input
            v-model="newOrgName"
            label="Organization Name"
            outlined
            autofocus
            :rules="[val => !!val || 'Name is required']"
            @keyup.enter="createOrganization"
          >
            <template #prepend>
              <q-icon name="business" />
            </template>
          </q-input>
          <div class="text-caption text-grey-7 q-mt-sm">
            You can add more details later by editing the organization.
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            flat
            label="Cancel"
            @click="cancelQuickCreate"
          />
          <q-btn
            color="primary"
            label="Create & Select"
            :loading="creating"
            :disable="!newOrgName.trim()"
            @click="createOrganization"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { organizationService } from '../services/organizationService';

  const props = defineProps({
    modelValue: {
      type: [Number, String],
      default: null,
    },
    type: {
      type: String,
      default: null,
      validator: (val) => [null, 'consultant', 'builder', 'contractor', 'competitor', 'customer'].includes(val),
    },
    label: {
      type: String,
      default: 'Organization',
    },
    required: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['update:modelValue', 'selected']);

  const $q = useQuasar();

  // State
  const loading = ref(false);
  const creating = ref(false);
  const searchTerm = ref('');
  const lastSearchTerm = ref(''); // Keep track of last non-empty search for create option
  const options = ref([]);
  const showQuickCreate = ref(false);
  const newOrgName = ref('');
  const selectedOrg = ref(null);

  // Computed
  const computedRules = computed(() => {
    if (props.required) {
      return [(val) => !!val || `${props.label} is required`];
    }
    return [];
  });

  const typeIcon = computed(() => {
    const icons = {
      consultant: 'engineering',
      builder: 'construction',
      contractor: 'business',
      competitor: 'people',
    };
    return icons[props.type] || 'business';
  });

  const filteredOptions = computed(() => {
    const opts = [...options.value];

    // Add "Create New" option if search term exists and no exact match
    if (searchTerm.value && searchTerm.value.trim().length > 0) {
      const exactMatch = opts.some(
        (o) => o.name.toLowerCase() === searchTerm.value.toLowerCase(),
      );
      if (!exactMatch) {
        opts.push({
          id: 'create-new',
          name: `Create "${searchTerm.value}"`,
          isCreateNew: true,
          searchTerm: searchTerm.value,
        });
      }
    }

    return opts;
  });

  // Methods
  async function onFilter(val, update, _abort) {
    searchTerm.value = val;

    if (!val || val.length < 1) {
      update(() => {
        options.value = [];
      });
      return;
    }

    loading.value = true;

    try {
      const results = await organizationService.searchOrganizations(val, props.type);

      update(() => {
        options.value = results.map((org) => ({
          ...org,
          primary_contact_name: org.primary_contact?.name || null,
        }));
      });
    } catch (_error) {
      update(() => {
        options.value = [];
      });
    } finally {
      loading.value = false;
    }
  }

  function onInputValue(val) {
    searchTerm.value = val;
    // Save last non-empty search term for when dropdown closes before onSelect
    if (val && val.trim()) {
      lastSearchTerm.value = val.trim();
    }
  }

  function onSelect(value) {
    // Skip if clearing the selection
    if (value === null || value === undefined) {
      selectedOrg.value = null;
      emit('update:modelValue', null);
      emit('selected', null);
      return;
    }

    // Check if "Create New" option was selected (by value or by option)
    if (value === 'create-new') {
      const createNewOpt = filteredOptions.value.find((o) => o.isCreateNew);
      if (createNewOpt) {
        newOrgName.value = createNewOpt.searchTerm;
      } else if (lastSearchTerm.value) {
        // Fallback: use lastSearchTerm when dropdown closed before we could access filteredOptions
        newOrgName.value = lastSearchTerm.value;
      }
      if (newOrgName.value) {
        showQuickCreate.value = true;
      }
      return; // Don't emit 'create-new' value to parent
    }

    const selected = filteredOptions.value.find((o) => o.id === value);

    if (selected?.isCreateNew) {
      newOrgName.value = selected.searchTerm;
      showQuickCreate.value = true;
      return;
    }

    selectedOrg.value = selected || null;
    emit('update:modelValue', value);
    emit('selected', selected);
  }

  async function createOrganization() {
    if (!newOrgName.value.trim()) {return;}

    creating.value = true;

    try {
      const newOrg = await organizationService.quickCreateOrganization(
        newOrgName.value.trim(),
        props.type,
      );

      $q.notify({
        type: 'positive',
        message: `Organization "${newOrg.name}" created successfully`,
        timeout: 3000,
      });

      // Select the newly created organization
      selectedOrg.value = newOrg;
      // Add the new org to options so it displays correctly
      options.value = [newOrg];
      emit('update:modelValue', newOrg.id);
      emit('selected', newOrg);

      // Close dialog and reset
      showQuickCreate.value = false;
      newOrgName.value = '';

    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create organization: ${error.message}`,
        timeout: 5000,
      });
    } finally {
      creating.value = false;
    }
  }

  function cancelQuickCreate() {
    showQuickCreate.value = false;
    newOrgName.value = '';
  }

  function getTypeColor(orgType) {
    const colors = {
      consultant: 'blue',
      builder: 'orange',
      contractor: 'green',
      competitor: 'purple',
    };
    return colors[orgType] || 'grey';
  }

  function formatType(orgType) {
    if (!orgType) {return '';}
    return orgType.charAt(0).toUpperCase() + orgType.slice(1);
  }

  // Watch for external value changes to load the org details
  watch(() => props.modelValue, async (newVal) => {
    // Skip if value is 'create-new' or not a valid ID
    if (newVal && newVal !== 'create-new' && !selectedOrg.value) {
      try {
        const org = await organizationService.getOrganization(newVal);
        selectedOrg.value = org;
        // Update options to include the selected org
        if (!options.value.find((o) => o.id === newVal)) {
          options.value = [org];
        }
      } catch (_error) {
        // Organization not found, ignore
      }
    }
  }, { immediate: true });
</script>

<style scoped>
.organization-autocomplete {
  width: 100%;
}
</style>
